/**
 * Phototron API Client
 * Handles communication with the Phototron REST API
 */

import { ApiClientConfig, AssetData } from './types';

const DEFAULT_BASE_URL = 'https://api.phototron.de';

export class ApiClient {
  private apiKey: string;
  private baseUrl: string;

  constructor(config: ApiClientConfig) {
    this.apiKey = config.apiKey;
    this.baseUrl = config.baseUrl || DEFAULT_BASE_URL;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-API-Key': this.apiKey,
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new ApiError(response.status, error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get asset by ID
   */
  async getAsset(assetId: string): Promise<AssetData> {
    return this.request<AssetData>(`/v1/assets/${assetId}`);
  }

  /**
   * Get texture URLs for an asset
   */
  async getTextures(assetId: string): Promise<AssetData['textures']> {
    const response = await this.request<{ textures: AssetData['textures'] }>(
      `/v1/assets/${assetId}/textures`
    );
    return response.textures;
  }

  /**
   * Get 3D model URL for an asset
   */
  async getModel(assetId: string): Promise<AssetData['model']> {
    const response = await this.request<{ model: AssetData['model'] }>(
      `/v1/assets/${assetId}/model`
    );
    return response.model;
  }

  /**
   * List all assets for the authenticated customer
   */
  async listAssets(options?: { page?: number; limit?: number }): Promise<{
    assets: AssetData[];
    total: number;
    page: number;
    limit: number;
  }> {
    const params = new URLSearchParams();
    if (options?.page) params.set('page', String(options.page));
    if (options?.limit) params.set('limit', String(options.limit));

    const query = params.toString();
    const endpoint = query ? `/v1/assets?${query}` : '/v1/assets';

    return this.request(endpoint);
  }

  /**
   * Validate API key
   */
  async validateKey(): Promise<{ valid: boolean; customer: string }> {
    return this.request('/v1/auth/validate');
  }
}

export class ApiError extends Error {
  public statusCode: number;

  constructor(statusCode: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
  }
}

// Singleton instance for convenience
let defaultClient: ApiClient | null = null;

export function initializeApi(config: ApiClientConfig): ApiClient {
  defaultClient = new ApiClient(config);
  return defaultClient;
}

export function getApiClient(): ApiClient {
  if (!defaultClient) {
    throw new Error('API client not initialized. Call initializeApi() first.');
  }
  return defaultClient;
}
